import React, { useEffect, useState } from "react";
import apiClient, { formatApiError } from "@/api/client";
import { Search, Copy, Download, Loader2, ListChecks, ChevronDown, ChevronRight, Megaphone, Sparkles } from "lucide-react";
import { toast } from "sonner";

const MT_LABEL = { exact: "Tam", phrase: "Sıralı", broad: "Geniş" };
const MT_COLOR = { exact: "#16A34A", phrase: "#4F46E5", broad: "#F59E0B" };

export default function PortalKeywords() {
  const [items, setItems] = useState([]);
  const [lists, setLists] = useState([]);
  const [adSets, setAdSets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);
  const [openListId, setOpenListId] = useState(null);
  const [openAdId, setOpenAdId] = useState(null);

  useEffect(() => {
    Promise.all([
      apiClient.get("/portal/keywords").then(r => r.data).catch(() => []),
      apiClient.get("/portal/keyword-lists").then(r => r.data).catch(() => []),
      apiClient.get("/portal/ad-copy-sets").then(r => r.data).catch(() => []),
    ]).then(([j, l, a]) => { setItems(j || []); setLists(l || []); setAdSets(a || []); }).finally(() => setLoading(false));
  }, []);

  const fmtKw = (kw, mt) => {
    const t = (mt || "broad").toLowerCase();
    if (t === "exact") return `[${kw}]`;
    if (t === "phrase") return `"${kw}"`;
    return kw;
  };
  const copyAll = (list, label) => {
    const text = list.map(k => fmtKw(k.keyword, k.match_type)).join("\n");
    navigator.clipboard.writeText(text);
    toast.success(`${label} (${list.length}) kopyalandı`);
  };
  const dlCsv = (list, name) => {
    const csv = "Keyword,Match Type,Group\n" + list.map(k => `"${(k.keyword||"").replace(/"/g,'""')}","${k.match_type||""}","${(k.group||"").replace(/"/g,'""')}"`).join("\n");
    const blob = new Blob([csv], {type:"text/csv;charset=utf-8"});
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = name; a.click();
  };

  if (loading) return <div className="p-10 text-center text-sm text-[#94A3B8]"><Loader2 size={16} className="animate-spin inline mr-2"/>Yükleniyor…</div>;

  return (
    <div className="space-y-5" data-testid="portal-keywords">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2"><Search size={20} className="text-[#4F46E5]"/>Anahtar Kelime Listeleri</h1>
        <p className="text-sm text-[#64748B] mt-1">Ajansınız tarafından sizin için hazırlanmış, AI destekli anahtar kelime analizleri.</p>
      </div>

      {items.length === 0 && lists.length === 0 && adSets.length === 0 && (
        <div className="ky-card p-12 text-center">
          <ListChecks size={32} className="mx-auto text-[#CBD5E1] mb-3"/>
          <div className="text-sm font-medium text-[#0F172A]">Henüz paylaşılmış içerik yok</div>
          <p className="text-xs text-[#94A3B8] mt-1">Ajansınız anahtar kelime analizleri, listeleri veya reklam metinleri hazırlayıp yayınladığında burada görünecek.</p>
        </div>
      )}

      <div className="space-y-3">
        {items.map(j => {
          const open = openId === j.id;
          const r = j.result || {};
          const pos = r.positive_keywords || []; const neg = r.negative_keywords || [];
          return (
            <div key={j.id} className="ky-card overflow-hidden">
              <button onClick={()=>setOpenId(open?null:j.id)} className="w-full p-4 flex items-center gap-3 text-left hover:bg-[#FAFAFB]">
                {open ? <ChevronDown size={16} className="text-[#64748B] shrink-0"/> : <ChevronRight size={16} className="text-[#64748B] shrink-0"/>}
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-sm truncate">{j.filename}</div>
                  <div className="text-[11px] text-[#94A3B8] mt-0.5">
                    {new Date(j.published_at || j.created_at).toLocaleString("tr-TR")}
                    {" · "}{pos.length} pozitif · {neg.length} negatif kelime
                  </div>
                </div>
              </button>
              {open && (
                <div className="border-t border-[#ECECEF] p-4 space-y-4">
                  {r.summary && (
                    <div className="bg-[#EEF2FF] border border-[#C7D2FE] rounded-lg p-3 text-xs text-[#3730A3] leading-relaxed">{r.summary}</div>
                  )}
                  <KwSection title="Pozitif Anahtar Kelimeler" items={pos} fmtKw={fmtKw} accent="#16A34A" onCopy={()=>copyAll(pos,"Pozitif")} onCsv={()=>dlCsv(pos,"pozitif-anahtar-kelimeler.csv")}/>
                  <KwSection title="Negatif Anahtar Kelimeler" items={neg} fmtKw={fmtKw} accent="#EF4444" onCopy={()=>copyAll(neg,"Negatif")} onCsv={()=>dlCsv(neg,"negatif-anahtar-kelimeler.csv")}/>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Saved Keyword Lists (positive/negative explicitly separated) */}
      {lists.length > 0 && (
        <div className="space-y-2 pt-3">
          <h2 className="text-lg font-bold flex items-center gap-2"><ListChecks size={18} className="text-[#16A34A]"/>Kayıtlı Listeler ({lists.length})</h2>
          <p className="text-xs text-[#94A3B8]">Ajansınız sizin için ayrı pozitif ve negatif kelime listeleri hazırladı.</p>
          {lists.map(l => {
            const open = openListId === l.id;
            const pos = l.positives || []; const neg = l.negatives || [];
            return (
              <div key={l.id} className="ky-card overflow-hidden" data-testid={`portal-kw-list-${l.id}`}>
                <button onClick={()=>setOpenListId(open?null:l.id)} className="w-full p-4 flex items-center gap-3 text-left hover:bg-[#FAFAFB]">
                  {open ? <ChevronDown size={16}/> : <ChevronRight size={16}/>}
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm truncate">{l.name}</div>
                    <div className="text-[11px] text-[#94A3B8] mt-0.5">{pos.length} pozitif · {neg.length} negatif · güncellendi {new Date(l.updated_at).toLocaleDateString("tr-TR")}</div>
                  </div>
                </button>
                {open && (
                  <div className="border-t border-[#ECECEF] p-4 space-y-4">
                    <KwSection title="Pozitif" items={pos} fmtKw={fmtKw} accent="#16A34A" onCopy={()=>copyAll(pos,"Pozitif")} onCsv={()=>dlCsv(pos,`${l.name}-pozitif.csv`)}/>
                    <KwSection title="Negatif" items={neg} fmtKw={fmtKw} accent="#EF4444" onCopy={()=>copyAll(neg,"Negatif")} onCsv={()=>dlCsv(neg,`${l.name}-negatif.csv`)}/>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Saved Ad Copy Sets */}
      {adSets.length > 0 && (
        <div className="space-y-2 pt-3">
          <h2 className="text-lg font-bold flex items-center gap-2"><Megaphone size={18} className="text-[#7C3AED]"/>Reklam Metinleri ({adSets.length})</h2>
          <p className="text-xs text-[#94A3B8]">Ajansınız sizin için reklam başlıkları ve açıklamaları hazırladı.</p>
          {adSets.map(s => {
            const open = openAdId === s.id;
            const headlines = s.headlines || []; const descs = s.descriptions || [];
            return (
              <div key={s.id} className="ky-card overflow-hidden" data-testid={`portal-ad-set-${s.id}`}>
                <button onClick={()=>setOpenAdId(open?null:s.id)} className="w-full p-4 flex items-center gap-3 text-left hover:bg-[#FAFAFB]">
                  {open ? <ChevronDown size={16}/> : <ChevronRight size={16}/>}
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm truncate flex items-center gap-2"><Sparkles size={12} className="text-[#7C3AED]"/>{s.name || `Reklam Seti #${s.id.slice(0,8)}`}</div>
                    <div className="text-[11px] text-[#94A3B8] mt-0.5">{headlines.length} başlık · {descs.length} açıklama · {new Date(s.created_at).toLocaleDateString("tr-TR")}</div>
                  </div>
                </button>
                {open && (
                  <div className="border-t border-[#ECECEF] p-4 space-y-4">
                    {headlines.length > 0 && (
                      <div>
                        <h4 className="text-xs font-semibold text-[#475569] mb-2 uppercase tracking-wider">Başlıklar ({headlines.length})</h4>
                        <div className="space-y-1.5">
                          {headlines.map((h, i) => (
                            <div key={i} className="text-sm bg-[#F8FAFC] border border-[#ECECEF] rounded-md px-3 py-2 flex items-center justify-between">
                              <span>{h.text || h.headline || h}</span>
                              {(h.char_count != null) && <span className="text-[10px] text-[#94A3B8] font-mono">{h.char_count}/30</span>}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {descs.length > 0 && (
                      <div>
                        <h4 className="text-xs font-semibold text-[#475569] mb-2 uppercase tracking-wider">Açıklamalar ({descs.length})</h4>
                        <div className="space-y-1.5">
                          {descs.map((d, i) => (
                            <div key={i} className="text-sm bg-[#F8FAFC] border border-[#ECECEF] rounded-md px-3 py-2 flex items-center justify-between gap-3">
                              <span className="flex-1">{d.text || d.description || d}</span>
                              {(d.char_count != null) && <span className="text-[10px] text-[#94A3B8] font-mono whitespace-nowrap">{d.char_count}/90</span>}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

const KwSection = ({ title, items, fmtKw, accent, onCopy, onCsv }) => {
  if (!items?.length) return null;
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <h3 className="font-semibold text-sm" style={{color: accent}}>{title} ({items.length})</h3>
        <div className="flex gap-2">
          <button onClick={onCopy} className="ky-btn ky-btn-outline ky-btn-sm"><Copy size={11}/>Kopyala</button>
          <button onClick={onCsv} className="ky-btn ky-btn-outline ky-btn-sm"><Download size={11}/>CSV</button>
        </div>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {items.map((k,i) => (
          <span key={i} title={k.rationale || ""} className="px-2 py-1 rounded-md text-xs font-mono bg-[#F8FAFC] border border-[#ECECEF] text-[#0F172A]">
            {fmtKw(k.keyword, k.match_type)}
            {k.match_type && <span className="ml-1 text-[9px] uppercase" style={{color:MT_COLOR[(k.match_type||"broad").toLowerCase()]}}>·{MT_LABEL[(k.match_type||"broad").toLowerCase()]}</span>}
          </span>
        ))}
      </div>
    </div>
  );
};
